import { useState } from 'react';

import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { displayToastSuccess } from '@/helpers/display-toast';
import { formatWeekdayWithShortDate } from '@/utils/date-formatters';

import { useCreateWorklog } from '../../hooks/use-time-track-mutations';
import { CreateWorklogRequest } from '../../types/dto/worklogs-summary.dto';
import { TimeEntryModal } from '../modals/time-entry-modal';

interface WeekHeaderQuickAddProps {
  /** Calendar date for the header cell — also the date pre-filled in the modal. */
  date: Date;
}

/**
 * Hover "+" rendered inside a header day cell of the week-view grid. Opens the
 * full `TimeEntryModal` in add-new mode with the cell's date pre-selected so
 * the user can log time on any day of the visible week without leaving the
 * Week view.
 */
export function WeekHeaderQuickAdd({ date }: WeekHeaderQuickAddProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  const [open, setOpen] = useState(false);
  const { mutateAsync } = useCreateWorklog();

  const handleSubmit = async (values: CreateWorklogRequest) => {
    await mutateAsync(values);
    displayToastSuccess(t('modal.addEntry.successAdd'));
  };

  return (
    <>
      <Button
        type="button"
        size="icon"
        variant="outline"
        aria-label={t('weekView.header.addOnDateAriaLabel', {
          date: formatWeekdayWithShortDate(date),
        })}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="absolute right-1 top-1 z-[2] h-[22px] w-[22px] opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
      >
        <Plus className="h-3 w-3" />
      </Button>
      <TimeEntryModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        initialDate={date}
      />
    </>
  );
}
